<template>
  <div class="clientsData-wrap">
    <div class="table-responsive" v-if="licenses && licenses.length > 0">
      <table class="table">
        <thead>
          <tr>
            <th>File Image</th>
            <th>State</th>
            <th>License Details</th>
            <th>Resident</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(license, index) in licenses" :key="index">
            <td>
              <div class="d-flex align-items-center">
                <img src="../../assets/images/quotes.svg" style="width: 20px; height: 20px; margin-right: 8px;" />
                {{ getFileName(license.license_doc) }}
              </div>
            </td>
            <td>{{ getStateName(license.license_state) }}</td>
            <td>
              <ul class="p-0 license-list">
                <li>License No: {{ license.license_number }}</li>
                <li>License Valid Until: {{ formatDate(license.license_exp_date) }}</li>
              </ul>
            </td>
            <td>{{ license.license_resident === 1 ? 'Permanent' : 'Non-Permanent' }}</td>
            <td>
              <div class="action-icons">
                <a href="#" @click.prevent="previewFile(license)" v-b-tooltip.hover title="Preview" class="me-2">
                  <i class="fas fa-eye"></i>
                </a>
                <a href="#" @click.prevent="openDeleteModal(index)" v-b-tooltip.hover title="Delete">
                  <i class="fas fa-trash"></i>
                </a>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else class="text-center py-4">
      <p class="mb-0">No licenses added yet.</p>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-license-modal" v-model="showDeleteModal" centered hide-header hide-footer
      class="delete-request-modal">
      <div class="text-center">
        <div class="d-flex justify-content-center mb-4">
          <img src="../../assets/images/exclaim.svg" />
        </div>
        <h4 class="mb-3">Delete License</h4>
        <p class="mb-2">Please note that once you delete this license, the action cannot be undone.</p>
        <p class="mb-4">Do you want to proceed with this delete request?</p>
        <div class="d-flex justify-content-center gap-3">
          <b-button variant="secondary" class="px-4" @click="showDeleteModal = false">
            Cancel
          </b-button>
          <b-button variant="danger" class="px-4" @click="confirmDelete">
            Delete
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- File Preview Modal -->
    <b-modal id="file-preview-modal" v-model="showPreviewModal" size="lg" centered hide-header hide-footer
      class="file-preview-modal">
      <div class="preview-container">
        <div class="preview-header">
          <h5 class="mb-0">{{ previewFileName }}</h5>
          <button type="button" class="btn-close" @click="showPreviewModal = false" aria-label="Close"></button>
        </div>
        <div class="preview-body" v-if="previewUrl">
          <div v-if="isImageFile" class="image-preview">
            <img :src="previewUrl" :alt="previewFileName" class="img-fluid" />
          </div>
          <div v-else-if="isPdfFile" class="pdf-preview">
            <iframe :src="previewUrl" width="100%" height="500px" frameborder="0"></iframe>
          </div>
          <div v-else class="no-preview">
            <p>Preview not available for this file type.</p>
            <a :href="previewUrl" :download="previewFileName" class="btn btn-primary">Download File</a>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import Helper from "@/resource/Helper";

export default {
  name: "UploadFileTable",
  props: {
    licenses: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showDeleteModal: false,
      deleteIndex: null,
      showPreviewModal: false,
      previewUrl: null,
      previewFileName: '',
      currentFileType: ''
    };
  },
  methods: {
    openDeleteModal(index) {
      this.deleteIndex = index;
      this.showDeleteModal = true;
    },
    confirmDelete() {
      if (this.deleteIndex !== null) {
        this.$emit('deleteLicense', this.deleteIndex);
        this.showDeleteModal = false;
        this.deleteIndex = null;
      }
    },
    getFileName(licenseDoc) {
      if (typeof licenseDoc === 'string') {
        return licenseDoc;
      } else if (licenseDoc && licenseDoc.name) {
        return licenseDoc.name;
      } else {
        return 'No file';
      }
    },
    getStateName(stateCode) {
      const stateList = Helper.getStatesList();
      return stateList[stateCode] || stateCode;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    previewFile(license) {
      const file = license.license_doc;
      
      if (typeof file === 'string') {
        // For existing files (string), we can't preview them without the actual URL
        Helper.infoMessage(this.$root, 'error', 'Preview not available for previously uploaded files.');
        return;
      }
      
      if (file && file instanceof File) {
        this.previewFileName = file.name;
        this.currentFileType = file.type;
        this.previewUrl = URL.createObjectURL(file);
        this.showPreviewModal = true;
      }
    },
    closePreviewModal() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      this.showPreviewModal = false;
      this.previewFileName = '';
      this.currentFileType = '';
    }
  },
  computed: {
    isImageFile() {
      return this.currentFileType && this.currentFileType.startsWith('image/');
    },
    isPdfFile() {
      return this.currentFileType === 'application/pdf';
    }
  },
  beforeDestroy() {
    // Clean up object URLs to prevent memory leaks
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
};
</script>
<style>
.license-list li {
  list-style: none;
  min-width: max-content;
}

.delete-request-modal .modal-dialog {
  max-width: 450px;
}

.delete-request-modal .modal-body {
  padding: 2.5rem 2rem;
}

.delete-request-modal h4 {
  color: #333;
  margin-bottom: 1.5rem;
  font-weight: 600;
  font-size: 1.25rem;
}

.delete-request-modal p {
  color: #666;
  margin-bottom: 0.75rem;
  line-height: 1.6;
  font-size: 0.95rem;
}

.delete-request-modal .fa-trash {
  margin-bottom: 1.5rem;
}

.delete-request-modal .btn {
  min-width: 120px;
  padding: 0.6rem 2rem;
  font-weight: 500;
}

.delete-request-modal .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.delete-request-modal .btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.file-preview-modal .modal-dialog {
  max-width: 90vw;
}

.preview-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.preview-header h5 {
  color: #333;
  font-weight: 600;
  margin: 0;
}

.preview-body {
  padding: 1rem;
  max-height: 80vh;
  overflow: auto;
}

.image-preview {
  text-align: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.pdf-preview {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.no-preview {
  text-align: center;
  padding: 3rem 2rem;
  color: #666;
}

.no-preview p {
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
}
</style>
